import { defaultsDeep, cloneDeep, uniq } from 'lodash'
import { User, UserTenantSettings } from '../models/user.entity'
import { DeepPartial, Page } from '../../../models/util.model'
import { UserSessionType } from '../models/user.types'
import { UsersListingKeys } from '../models/user-listing.model'

/**
 * The user initial state
 */
const USER_INITIAL_STATE: DeepPartial<User> = {
  type: 'HUMAN',
  settings: {
    pickingLists: {},
    goodsReceives: {},
  },
}

/**
 * Initialize a partial user
 * @param user- the partial user
 * @returns the user updated
 */
export function initUser(user: Partial<User> = {}): User {
  return defaultsDeep(cloneDeep(user), USER_INITIAL_STATE)
}

/**
 * Get user tenant settings by tenant ID
 * @param user - the user
 * @param tenantId - the tenant ID
 * @returns the user tenant settings
 */
export function getUserTenantSettings(
  user: User,
  tenantId: string,
): UserTenantSettings | undefined {
  return user.tenants?.find((t) => t._id === tenantId)?.settings
}

/**
 * Set user tenant settings
 * @param user - the current user
 * @param tenantId - the tenant ID
 * @param settings - the settings to update
 * @returns the user updated
 */
export function setUserTenantSettings(
  user: User,
  tenantId: string,
  settings: UserTenantSettings,
): User {
  const userTenant = user.tenants?.find((t) => t._id === tenantId)
  if (!userTenant) {
    return user
  }

  return {
    ...user,
    tenants: user.tenants?.map((t) =>
      t._id === tenantId
        ? {
            ...t,
            settings,
          }
        : t,
    ),
  }
}

/**
 * Return a boolean that indicates if a session type is allowed
 * @param user - the user
 * @param sessionType - the session type
 * @returns true if a session type is allowed otherwise false
 */
export function checkUserSessionAllowed(
  user: User,
  sessionType: UserSessionType,
): boolean {
  return !!user.allowedSessions?.includes(sessionType)
}

/**
 * Parse user sessions allowed into a dictionary
 * @param user - the user
 * @returns the dictionary with the session allowed
 */
export function parseUserSessionsAllowed(user: User): {
  [sessionType: string]: boolean
} {
  if (!user.allowedSessions) {
    return {}
  }

  return user.allowedSessions.reduce(
    (acc, s) => ({
      ...acc,
      [s]: true,
    }),
    {},
  )
}

export function parseUser(user?: User): string {
  return user ? capitalize(user.name) + ' ' + capitalize(user.surname) : ''
}

const capitalize = (str?: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''

/**
 * Parse user page keys
 * @param page - the users page
 * @returns the page updated
 */
export function parseUsersPageKeys(
  page: Page<User>,
  tenantId: string,
): UsersListingKeys {
  return page.data.reduce<UsersListingKeys>(
    (acc, r) => parseUserKeys(r, acc, tenantId),
    {},
  )
}

/**
 * Parse user external data
 * @param user - the replenishment
 * @param extData - the external data object
 * @returns the extData updated
 */
export function parseUserKeys(
  user: User,
  extData: UsersListingKeys = {},
  tenantId: string,
): UsersListingKeys {
  const userRolesIds = user.roles
    ?.filter((r) => r.tenantId === tenantId)
    .map((r) => r._id)
  const roleIds = uniq([...(extData['roleIds'] || []), ...(userRolesIds || [])])

  return {
    roleIds,
  }
}
